import React from "react";
import { addDays } from "../extensions/helpers";

interface StoredValue<T> {
  value: T;
  expiry: number | null;
}

/**
 * Hostnames that are definitely NOT the live store: `netlify dev` and
 * `npm start` on localhost, and Netlify deploy previews. These can be pointed
 * at the Square SANDBOX account, whose catalog object ids are completely
 * different from production's.
 *
 * Deliberately a DENYLIST, not an allowlist of production domains. Get an
 * allowlist wrong — a Netlify primary-domain switch, a second apex, a new
 * `.shop` — and every live visitor silently gets fresh keys, losing their cart,
 * wishlist and login with nothing to indicate why. Getting this denylist "wrong"
 * costs nothing instead: any hostname that isn't listed is a real domain on its
 * own origin, and origins already have separate storage.
 */
function isDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".netlify.app")
  );
}

/**
 * Namespaces a browser-storage key by environment.
 *
 * localStorage is already origin-scoped, so `localhost:8888` can never touch
 * what `americanaandcompany.com` stored — that isolation is free, and it's why
 * the suffix only ever does real work on **localhost**: every other hostname is
 * a distinct origin with its own store regardless.
 *
 * What this guards is the one case origin scoping does NOT cover — a single
 * origin serving two environments over time. Flip `SQUARE_ENVIRONMENT` in
 * `.env.local` and localhost would otherwise replay the *other* environment's
 * catalog objects, carts and session out of its own cache: dead
 * `catalogObjectId`s and images that 404.
 *
 * Any real domain gets the key unchanged, so live visitors keep their cart,
 * wishlist and login across this change.
 */
export function scopedStorageKey(key: string): string {
  if (typeof window === "undefined") return key;
  return isDevHost(window.location.hostname) ? `${key}-dev` : key;
}

function getStorageValue<T>(key: string, defaultValue: T): T {
  if (typeof window === "undefined") return defaultValue;

  const saved = localStorage.getItem(key);
  if (saved !== null) {
    try {
      const parsed: StoredValue<T> = JSON.parse(saved);
      if (parsed.expiry === null || Date.now() < parsed.expiry) {
        return parsed.value;
      } else {
        localStorage.removeItem(key); // expired
      }
    } catch {
      localStorage.removeItem(key); // corrupted
    }
  }
  return defaultValue;
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  expiryTimestamp?: number, // absolute timestamp
): [T, React.Dispatch<React.SetStateAction<T>>, boolean] {
  const storageId = React.useMemo(() => scopedStorageKey(key), [key]);
  const [value, setValue] = React.useState<T>(() => {
    return getStorageValue(storageId, defaultValue);
  });
  const [isHydrated, setIsHydrated] = React.useState(false);
  // Skip the first effect run so that hydrating from cache doesn't overwrite
  // the stored expiry with a freshly-computed timestamp.
  const isFirstRender = React.useRef(true);
  // The expiry is read through a ref so it is NOT an effect dependency. Callers
  // routinely compute it inline (`getRelativeTTL(2)` in CatalogContext), which
  // yields a new timestamp on every render — as a dependency that made this
  // effect fire on every render and re-write the CURRENT value to storage with
  // a rolling TTL. A tab holding a stale catalog therefore kept stamping it
  // back over a fresh one written by another tab (and its "2 hour" TTL never
  // actually elapsed while the tab stayed open). Writes now happen only when
  // the value itself changes.
  const expiryRef = React.useRef(expiryTimestamp);
  expiryRef.current = expiryTimestamp;

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const item: StoredValue<T> = {
      value,
      expiry: expiryRef.current ?? null,
    };
    localStorage.setItem(storageId, JSON.stringify(item));
  }, [storageId, value]);

  // Set hydration flag once value is initialized
  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  return [value, setValue, isHydrated];
}

export const getLocalStorageExpiryTime = (
  hour?: number,
  min?: number,
  sec?: number,
  ms?: number,
): number => {
  let expiryTime = new Date();
  expiryTime.setHours(hour ?? 0, min ?? 0, sec ?? 0, ms ?? 0);

  // If already after that time - set tomorrow
  if (expiryTime < new Date()) {
    expiryTime = addDays(expiryTime, 1);
  }

  return expiryTime.getTime();
};

/** Returns an absolute expiry timestamp N hours (and optional minutes) from now. */
export const getRelativeTTL = (hours: number, minutes = 0): number => {
  return Date.now() + (hours * 60 + minutes) * 60 * 1000;
};
